import { Alert, Box, Button, Typography } from '@mui/material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

type GridAlertContainerProps = {
  gridError?: FetchBaseQueryError | SerializedError | undefined;
  parsedColumnsCount: number;
  onRetryGrid?: () => void;
};

export const GridAlertContainer = ({
  gridError,
  parsedColumnsCount,
  onRetryGrid,
}: GridAlertContainerProps) => {
  const formatError = (error: FetchBaseQueryError | SerializedError | undefined): string => {
    if (!error) return '';
    if ('status' in error) {
      return 'error' in error ? String(error.error) : `Error: ${error.status}`;
    }
    if ('message' in error) return error.message ?? '';
    return String(error);
  };

  const gridErrorMsg = gridError ? formatError(gridError) : '';

  return (
    <>
      {parsedColumnsCount === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No columns parsed from metadata. Check browser console for details.
        </Alert>
      )}

      {gridError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            onRetryGrid && (
              <Button color="inherit" size="small" onClick={onRetryGrid}>
                Retry
              </Button>
            )
          }
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Failed to load grid data
            </Typography>
            <Typography variant="body2">{gridErrorMsg}</Typography>
            {gridErrorMsg.includes('timeout') && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                This grid has a large amount of data. Try applying filters to reduce the dataset
                size.
              </Typography>
            )}
          </Box>
        </Alert>
      )}
    </>
  );
};
